import { ApolloError, useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { groupBy, intersectionWith, startCase } from 'lodash'
import { useSubscriptionErrorReset } from 'hooks/useResetErrorSubscription'
import { useFilterSchema } from 'hooks/useFilterSchema'
import { Cell } from 'reducers/subscription/user'
import { Answer, Filter, Schema } from 'typings/filters'
import { Segment } from 'typings/segments'
import { Data, DataPoint } from 'components/Charts/types'
import { getSegmentVisibility } from 'reducers/audienceProfiler/selectors'
import { GET_FILTER_VALUES, SOLITARY_SINGLE_QUERY } from 'query/query'
import { SEGMENT_VARIABLE_NAME } from 'hooks/useSegmentLeaderboardKPIs'
import { useAppliedFiltersAndContext } from 'hooks/useAppliedFiltersAndContext'
import { StaticSegmentData, staticSegmentDataMap } from './config'
import { SampleSizeState } from './utils'
import { InsightData } from 'containers/charts'

export interface FilterValuesResult {
  filterValues: Answer[]
}

export interface SegmentTotals {
  weighted?: number
  unweighted?: number
}

export interface AudienceMetadata {
  allHaveLowSampleSize: boolean
  allHaveNoData: boolean
  someHaveLowSampleSize: boolean
  someHaveNoData: boolean
  allHaveNoOrLowSampleData: boolean
}

export interface AudienceSegments {
  loading: boolean
  error?: ApolloError
  data?: AudienceSegment[]
  segmentTotals?: SegmentTotals
  visibleAudienceSegments?: AudienceSegment[]
  meta?: AudienceMetadata
}

export const SEGMENT_VARIABLE_ID = 666

export type AudienceSegment = {
  label: string
  segment: Segment
  sampleSizeState: SampleSizeState
  segmentDataPoint?: DataPoint
  filteredOut: boolean
  isVisible: boolean
} & StaticSegmentData

const getSampleSizeState = (dataPoint?: DataPoint): SampleSizeState => {
  if (!dataPoint?.unweighted) {
    return SampleSizeState.NoData
  }

  if (dataPoint.unweighted < 50) {
    return SampleSizeState.LowSampleSize
  }

  return SampleSizeState.Ok
}

const getAudienceMetadata = (
  segments: AudienceSegment[] = [],
  appliedSegmentFilters?: number,
  visibleAudienceSegments: AudienceSegment[] = []
): AudienceMetadata | undefined => {
  if (!segments.length) {
    return
  }
  const segmentIDList = visibleAudienceSegments.map((s) => s.segment)
  const filteredSegments = segments.filter((obj) => segmentIDList.includes(obj.segment))
  const {
    [SampleSizeState.LowSampleSize]: lowSampleSizeSegments = [],
    [SampleSizeState.NoData]: noDataSegments = [],
  } = groupBy(filteredSegments, 'sampleSizeState')
  return {
    allHaveLowSampleSize: lowSampleSizeSegments.length === filteredSegments.length,
    allHaveNoData: noDataSegments.length === filteredSegments.length,
    someHaveLowSampleSize: lowSampleSizeSegments.length > 0,
    someHaveNoData: appliedSegmentFilters === 0 ? false : noDataSegments.length > 0,
    allHaveNoOrLowSampleData:
      lowSampleSizeSegments.length + noDataSegments.length === filteredSegments.length,
  }
}

const shouldSkipQuery = (context: Cell[] | null, filterSchema: Schema): boolean => {
  return !context || filterSchema === Schema.CIE_REVIEW
}
const getEffectiveValues = (
  insightData: { insightData: InsightData } | undefined,
  context: Cell[],
  appliedFilters: Filter[],
  filterSchema: Schema
) => {
  const effectiveContext = insightData?.insightData?.graphqlVars.context ?? context
  const effectiveFilters = insightData?.insightData?.graphqlVars?.filters ?? appliedFilters
  const shouldSkip = shouldSkipQuery(effectiveContext, filterSchema ?? Schema.CIE)
  return { effectiveContext, effectiveFilters, shouldSkip }
}

export const useAudienceSegments = (
  localFilters?: Pick<Filter, 'variable_id' | 'answer_code'>[],
  insightData?: { insightData: InsightData }
): AudienceSegments => {
  const { appliedFilters, context } = useAppliedFiltersAndContext()
  const { filterSchema } = useFilterSchema()
  const { effectiveContext, effectiveFilters, shouldSkip } = getEffectiveValues(
    insightData,
    context as Cell[],
    appliedFilters as Filter[],
    filterSchema
  )

  const {
    data: segmentsAsFilters,
    loading: filtersLoading,
    error: filtersError,
  } = useQuery<FilterValuesResult>(GET_FILTER_VALUES, {
    variables: { context: effectiveContext, variableId: SEGMENT_VARIABLE_ID, filters: [] },
    fetchPolicy: 'cache-first',
    skip: shouldSkip,
  })
  const appliedSegmentFilters = useMemo(
    () =>
      intersectionWith(
        segmentsAsFilters?.filterValues,
        appliedFilters,
        (a, b) => a.answer_code === b.answer_code && b.variable_id === SEGMENT_VARIABLE_ID
      ),
    [appliedFilters, segmentsAsFilters?.filterValues]
  )
  const segmentVisibility = useSelector(getSegmentVisibility)

  const {
    data: segments,
    loading: segmentsLoading,
    error: segmentsError,
  } = useQuery<Data>(SOLITARY_SINGLE_QUERY, {
    variables: {
      primaryVar: SEGMENT_VARIABLE_NAME,
      filters: effectiveFilters,
      context: effectiveContext,
      localFilters,
    },
    skip: shouldSkip,
  })
  const error = filtersError ?? segmentsError
  const segmentTotals = {
    weighted: segments?.cross?.totalCount,
    unweighted: segments?.cross?.totalCountUnweighted,
  }
  const loading = filtersLoading || segmentsLoading
  const data = useMemo(
    () =>
      !loading
        ? segmentsAsFilters?.filterValues.reduce<AudienceSegment[]>(
            (acc, { label, answer_id, answer_code }) => {
              // Exclude unknown segments from segment data
              if (Object.values(Segment).includes(answer_id)) {
                const segment = answer_id as Segment

                const segmentDataPoint = segments?.cross?.summarySeries.dataPoints.find(
                  (dataPoint) => dataPoint.secondaryCode === segment
                )

                const sampleSizeState = getSampleSizeState(segmentDataPoint)

                // At least one Audience Segment filter has been applied, and this segment is NOT one of those
                const filteredOut =
                  appliedSegmentFilters.length > 0 &&
                  !appliedSegmentFilters.find(
                    (filter: Answer) => filter.answer_code === answer_code
                  )

                const isVisible = segmentVisibility[segment]

                acc.push({
                  ...staticSegmentDataMap[segment],
                  segment,
                  label: startCase(label),
                  sampleSizeState,
                  segmentDataPoint,
                  filteredOut,
                  isVisible,
                })
              }

              return acc
            },
            []
          )
        : undefined,
    [
      appliedSegmentFilters,
      loading,
      segmentVisibility,
      segments?.cross?.summarySeries.dataPoints,
      segmentsAsFilters?.filterValues,
    ]
  )

  const visibleAudienceSegments = useMemo(
    () =>
      data !== undefined
        ? data.filter(
            (d) =>
              d.isVisible &&
              (appliedSegmentFilters.length === 0 ||
                appliedSegmentFilters.find((filter) => filter.answer_id === d.segment))
          )
        : undefined,
    [appliedSegmentFilters, data]
  )
  useSubscriptionErrorReset(error)

  return {
    data,
    visibleAudienceSegments,
    segmentTotals,
    loading,
    error: !segments ? error : undefined,
    meta: getAudienceMetadata(data, appliedSegmentFilters.length, visibleAudienceSegments),
  }
}
